import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
  Divider,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  ContactMail as ContactMailIcon,
  HowToReg as HowToRegIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse, describeApiError } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { DataTable, FilterBar, SchoolPicker, EmptyState } from './primitives';
import { useQueryState } from '../../hooks/useQueryState';
import { ROLES, ROLE_LABELS, isMasterAdmin, isSchoolAdmin, isTeacher } from '../../constants/roles';
import RoleChip from '../RoleChip';
import { formatTimestamp } from './SafetyFlagsView';

// One funnel — sign-up requests (people asking for an account) above,
// email invites (accounts already offered, awaiting acceptance) below —
// replacing the old, separately-navigable AccessRequestsView.js and
// InvitationsView.js (admin console v2 Phase 2). Merged because they're one
// story read top to bottom: a request becomes an invite the moment it's
// approved. Both former files have been deleted (this branch removes them
// outright rather than leaving dead code behind); AdminDashboardV2.js's
// `/admin/requests` route now redirects here instead of rendering
// AccessRequestsView. See this file's two section components
// (SignupRequestsSection / InvitesSection) below.

// ---------------------------------------------------------------------------
// Sign-up requests — master-admin only (GET /api/access-inquiries is gated
// by _require_inquiry_admin == SUPER_ADMIN_ROLES; see app/routes/users.py).
// ---------------------------------------------------------------------------

// Backend `last_error` strings (Resend's own bounce/failure reasons) aren't
// guaranteed to end with terminal punctuation. Used everywhere one gets
// spliced into a sentence we wrote, so a value with no trailing period
// doesn't run on into whatever follows it instead of reading as a
// finished thought.
function ensureSentence(s) {
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

// Roles POST /api/access-inquiries/{id}/approve will actually accept
// (schemas.ApproveInquiryRequest / VALID_ROLES) — MASTER_ADMIN is
// deliberately not invite-able through this or any invite flow, so this is
// a fixed list rather than constants/roles.js's assignableRoles(), which
// would include it for a MASTER_ADMIN caller.
const APPROVE_ROLE_OPTIONS = [ROLES.STUDENT, ROLES.TEACHER, ROLES.SCHOOL_ADMIN];

// The only filter this page has that USED to live in local `useState` (a
// bare on/off Chip) instead of the URL like every other filter on this
// page (Invites' own `q`/`status`/`page` below) — deep-linking/back button
// silently dropped it, and a toggling Chip was also a different
// interaction language from the Select-based filters used everywhere else
// in the admin console. Given its own useQueryState (rather than folding
// into INVITES_QUERY_DEFAULTS below) since it filters a completely
// different list (sign-up requests, not invites) that happens to share
// this page/URL.
const REQUESTS_QUERY_DEFAULTS = { requestsFilter: 'new' };

function ApproveInquiryDialog({ open, inquiry, currentUserRole, onClose, onApproved, notify }) {
  const navigate = useNavigate();
  const [role, setRole] = useState(ROLES.STUDENT);
  const [school, setSchool] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (open) {
      setRole(ROLES.STUDENT);
      setSchool('');
      setFormError('');
    }
  }, [open, inquiry?.id]);

  const handleSubmit = async () => {
    if (!school) {
      setFormError('Choose a school.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/access-inquiries/${inquiry.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, school: school.toUpperCase() }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      onApproved(inquiry.id, data);
      onClose();
    } catch (err) {
      setFormError(describeAdminError(err, "Couldn't approve this request."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!inquiry) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Approve &amp; send invite</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 2 }}>
          Sends an invitation to <strong>{inquiry.email}</strong>. They'll get an email with a link to create their account.
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="approve-role-label">Role</InputLabel>
            <Select
              labelId="approve-role-label"
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
            >
              {APPROVE_ROLE_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>{ROLE_LABELS[r] || r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <SchoolPicker
            value={school}
            onChange={setSchool}
            currentUserRole={currentUserRole}
            required
            allowUnassigned={false}
            label="School"
          />
          {formError && <Alert severity="error">{formError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting} sx={TOUCH_HIT_SX}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark }, ...TOUCH_HIT_SX }}
        >
          Approve &amp; send invite
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// A single request card. `resultInfo` (once set, after Approve/Decline
// succeeds this session) collapses the row to a closed, read-only summary
// instead of removing it from the list outright — the admin who just acted
// on it gets visible confirmation of what happened, without waiting for a
// full refetch to notice. It resets on the next real page load/manual
// refresh, when the backend's own `status` (NEW vs HANDLED) takes back over
// as the source of truth for what's still pending.
//
// `warning` is a separate, non-terminal case: an Approve whose invite send
// genuinely FAILED leaves the inquiry at NEW server-side (see
// approve_access_inquiry's docstring) — the row must stay actionable, not
// collapse, so this renders as an inline Alert on the still-open card
// instead of ever reaching the `resultInfo` branch below.
// AccessInquiry.email_status tracks a *different* email than the invite
// chip below tracks: the notification Resend sends to the platform inbox
// when someone submits this request, not anything addressed to the
// requester. There's no per-row resend for it — the request itself is
// already sitting right here in the console either way — so this is purely
// informational, and only worth a line when something's actually wrong: a
// silently-suppressed or bounced notification is exactly how staff can miss
// that a request came in at all (the incident that motivated tracking this
// in the first place). UNSENT/SKIPPED/SENT/DELIVERED render nothing — a
// healthy or merely-in-flight notification isn't news when you're already
// looking at the request.
//
// Renders as an Alert (see SignupRequestCard below), not a chip — the
// `warning` prop nine lines below it is already doing exactly this job
// (a banner-shaped notice on this same card), and MUI's Alert gives every
// severity an icon for free, a second channel besides hue for colorblind
// readers. `severity` mirrors the same three-tier read as the invite
// chip's colors: error for the ones that mean staff plausibly never saw
// this request (FAILED/BOUNCED/SUPPRESSED), warning for DELAYED (still
// trying), info for COMPLAINED — deliberately the quietest severity, not
// error, since there's nothing actionable about it here.
function inquiryNotificationNotice(inquiry) {
  switch (inquiry.email_status) {
    case 'FAILED':
      return { severity: 'error', text: `Staff notification email failed to send${inquiry.last_error ? ` — ${ensureSentence(inquiry.last_error)}` : '.'}` };
    case 'BOUNCED':
      return { severity: 'error', text: `Staff notification email bounced${inquiry.last_error ? ` — ${ensureSentence(inquiry.last_error)}` : '.'}` };
    case 'SUPPRESSED':
      return { severity: 'error', text: "Staff notification email was suppressed by Resend — the platform inbox may be on a suppression list. Clear it in the Resend dashboard so future request notifications go through." };
    case 'COMPLAINED':
      return { severity: 'info', text: 'The platform inbox marked this request\'s notification email as spam.' };
    case 'DELAYED':
      return { severity: 'warning', text: 'Staff notification email is delayed — Resend is still retrying.' };
    default:
      return null;
  }
}

function SignupRequestCard({ inquiry, resultInfo, warning, busy, onApprove, onDecline }) {
  if (resultInfo) {
    const approved = resultInfo.type === 'approved';
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)', display: 'flex', alignItems: 'center', gap: 1.25, opacity: 0.75 }}
      >
        {approved ? (
          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20, flexShrink: 0 }} />
        ) : (
          <CancelIcon sx={{ color: adminColors.inkSoft, fontSize: 20, flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }} noWrap>
            {inquiry.name || inquiry.email}
          </Typography>
          <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>
            {approved ? `Invite sent to ${inquiry.email} · request closed` : 'Declined · request closed'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  const notice = inquiryNotificationNotice(inquiry);

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)', display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: adminColors.ink }}>
            {inquiry.name || 'No name given'}
          </Typography>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>{inquiry.email}</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: adminColors.inkSoft, whiteSpace: 'nowrap' }}>
          {formatTimestamp(inquiry.created_at)}
        </Typography>
      </Box>

      {notice && (
        <Alert severity={notice.severity} data-testid="inquiry-notification-notice" sx={{ py: 0.5 }}>
          {notice.text}
        </Alert>
      )}

      {inquiry.message && (
        <Typography
          variant="body2"
          sx={{ color: adminColors.ink, whiteSpace: 'pre-wrap', bgcolor: adminColors.surface, borderRadius: radius.control, px: 1.5, py: 1, lineHeight: 1.5 }}
        >
          &ldquo;{inquiry.message}&rdquo;
        </Typography>
      )}

      {warning && <Alert severity="warning" sx={{ mt: 0.5 }}>{warning}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap', mt: 0.5 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onDecline}
          disabled={busy}
          sx={{ color: adminColors.inkSoft, borderColor: 'rgba(24,24,27,0.16)', ...TOUCH_HIT_SX }}
        >
          {busy === 'decline' ? <CircularProgress size={16} /> : 'Decline'}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onApprove}
          disabled={Boolean(busy)}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark }, ...TOUCH_HIT_SX }}
        >
          Approve → send invite
        </Button>
      </Box>
    </Paper>
  );
}

function SignupRequestsSection({ currentUser, notify }) {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reqQs, setReqQueryState] = useQueryState(REQUESTS_QUERY_DEFAULTS);
  const showAll = reqQs.requestsFilter === 'all';
  const [busyId, setBusyId] = useState(null); // { id, action: 'decline' } — only Decline needs an inline spinner; Approve's is inside the dialog
  const [approveTarget, setApproveTarget] = useState(null);
  // Requests acted on this session — see SignupRequestCard's own comment on
  // why this isn't just re-derived from a refetch.
  const [resultById, setResultById] = useState({});

  const fetchInquiries = useCallback(() => {
    setLoading(true);
    setError('');
    return authFetch(`${config.API_BASE_URL}/api/access-inquiries`)
      .then((res) => handleApiResponse(res, navigate))
      .then((data) => {
        if (data === null) return; // 401 — already redirected
        setInquiries(data || []);
      })
      .catch((err) => setError(`Couldn't load sign-up requests. ${describeApiError(err)}`))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const handleDecline = async (inquiry) => {
    setBusyId(inquiry.id);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/access-inquiries/${inquiry.id}/handled`, { method: 'PATCH' });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      setResultById((prev) => ({ ...prev, [inquiry.id]: { type: 'declined' } }));
      notify('Request declined.', 'success');
    } catch (err) {
      notify(`Couldn't decline this request. ${describeAdminError(err)}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleApproved = (inquiryId, data) => {
    const invitation = data?.invitation;
    if (data?.status === 'HANDLED') {
      setResultById((prev) => ({ ...prev, [inquiryId]: { type: 'approved' } }));
      if (invitation?.email_status === 'FAILED') {
        notify(`Invite created for this request, but delivery is currently failing. It can be resent from Invites below.`, 'warning');
      } else if (invitation?.email_status === 'SKIPPED') {
        notify(`Invite created, but no email was sent — delivery isn't configured on this server.`, 'warning');
      } else {
        notify('Invite sent. Request closed.', 'success');
      }
    } else {
      // Send genuinely FAILED — the backend deliberately leaves the inquiry
      // at NEW so it keeps surfacing (see approve_access_inquiry's
      // docstring). Don't collapse the row; tell the admin what happened
      // and that re-approving will retry the same invite.
      setInquiries((prev) =>
        prev.map((i) => (i.id === inquiryId ? { ...i, _warning: `Invite delivery failed (${invitation?.last_error || 'unknown error'}). The request stays open — Approve again to retry.` } : i))
      );
      notify("Invite delivery failed. This request stays open — try Approve again once delivery is restored.", 'error');
    }
  };

  const displayed = inquiries.filter((i) => i.status === 'NEW' || resultById[i.id] || showAll);
  const newCount = inquiries.filter((i) => i.status === 'NEW').length;

  return (
    <Box component="section" sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink }}>Sign-up requests</Typography>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
            People who asked for an account from the sign-in page.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="requests-filter-label">Show</InputLabel>
          <Select
            labelId="requests-filter-label"
            label="Show"
            value={reqQs.requestsFilter}
            onChange={(e) => setReqQueryState({ requestsFilter: e.target.value })}
            sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
          >
            <MenuItem value="new">{`New (${newCount})`}</MenuItem>
            <MenuItem value="all">Showing all</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchInquiries}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : displayed.length === 0 ? (
        <EmptyState
          icon={<ContactMailIcon />}
          title="No sign-up requests"
          description="Requests submitted from the sign-in page will show up here."
        />
      ) : (
        <Stack spacing={1.25}>
          {displayed.map((inquiry) => (
            <SignupRequestCard
              key={inquiry.id}
              inquiry={inquiry}
              resultInfo={resultById[inquiry.id]}
              warning={inquiry._warning}
              busy={busyId === inquiry.id ? 'decline' : null}
              onApprove={() => setApproveTarget(inquiry)}
              onDecline={() => handleDecline(inquiry)}
            />
          ))}
        </Stack>
      )}

      <ApproveInquiryDialog
        open={Boolean(approveTarget)}
        inquiry={approveTarget}
        currentUserRole={currentUser?.role}
        onClose={() => setApproveTarget(null)}
        onApproved={handleApproved}
        notify={notify}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Invites — every role that can send one (STAFF_ROLES) sees this half,
// scoped exactly as GET /api/invitations already scopes it server-side:
// TEACHER sees only invites they personally sent, SCHOOL_ADMIN their own
// school, MASTER_ADMIN everyone.
// ---------------------------------------------------------------------------

const INVITES_PAGE_SIZE = 20;

const INVITES_QUERY_DEFAULTS = { q: '', status: '', page: 1 };

const INVITE_STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// One chip's worth of honest status per invite, folding the old two-chip
// "status + email delivery" display (see the now-unused InvitationsView.js)
// into a single label. `inv.status` (PENDING/ACCEPTED/EXPIRED/CANCELLED) is
// the invite's own lifecycle; `inv.email_status` is a second, independent
// axis — whether the *email* actually got there. It matters most while
// `status` is still PENDING (the label and color both key off it there),
// but it doesn't stop mattering the moment an invite EXPIRES — a bounced,
// failed, or suppressed send that then expired is still worth knowing
// about, so EXPIRED keeps its own label/color (the lifecycle fact — the
// window closed — shouldn't change) but still carries whatever hint
// email_status earns it below, via the same emailStatusHint() both
// branches read from.
//
// Nine email_status values is too many to color individually — a chip list
// scanned quickly needs a handful of *meanings*, not nine hues. So this
// collapses onto the four colors this file already had, reused by what the
// admin actually needs to do about each:
//   pending (indigo)   — nothing to do right now, an outcome may still land
//                         (UNSENT, SKIPPED, SENT, DELAYED)
//   accepted (green)   — confirmed good, no problem here (also DELIVERED —
//                         see the `accepted`/`delivered` note below on why
//                         these two share one hue rather than two)
//   failed (red)       — the admin should act (FAILED, BOUNCED, SUPPRESSED)
//   cancelled (grey)   — over, informational, nothing to do AND nothing to
//                         retry (COMPLAINED — re-sending to someone who
//                         just marked this as spam is a compliance problem,
//                         not a delivery problem; see the Actions column's
//                         canResend below, the one email_status Resend is
//                         withheld for)
// The label always leads with the concrete word (Sent/Delivered/Delayed/
// Bounced/Suppressed/Marked as spam/Failed) rather than a bare "Pending",
// and the *reason* lives in the hover/focus Tooltip rather than the label
// itself — see emailStatusHint()'s own comment on why. The three
// admin-must-act reds (Bounced/Suppressed/Failed) additionally get a short
// "· fragment" second word in the label itself, the same secondary-text
// pattern "Sent · expires …" already uses — these are the rows where
// noticing *is* the job, and that shouldn't cost a hover or Tab stop.
const INVITE_STATUS_STYLES = {
  accepted: { bg: '#e8f5e9', color: '#2e7d32' },
  pending: { bg: '#eef2ff', color: '#4338ca' },
  // #e65100 on #fff3e0 measures 3.46:1 at this chip's 13px/600 weight —
  // below the 4.5:1 AA floor for text. adminColors.warningAlertBg is the
  // exact same orange hue, darkened until it clears AA (5.36:1) — already
  // used for this codebase's other orange-on-pale text (SafetyFlagsView's
  // PENDING chip); reused verbatim here instead of a second, independently
  // -tuned orange.
  //
  // Background is Tailwind amber-100 (#fef3c7) rather than the paler
  // #fff3e0 this used to be — reused from SafetyFlagsView's own
  // SEVERITY_CONFIG.medium rather than invented here. The swap is a small,
  // deliberate nudge, not a fix: measured against `failed`'s #fef2f2, the
  // two pale fills sit at ~1.0:1 contrast from each other under normal
  // vision AND simulated protanopia/deuteranopia alike (Viénot/Brettel
  // matrices) — amber-100 only moves that to ~1.02:1. Any shift big enough
  // to matter would mean abandoning the pale-tint chip language every
  // other status in this file uses, which is a worse trade than accepting
  // that these two rows were never meant to be told apart by background
  // hue alone: the label text ("Expired 8/19/2026" vs a word like
  // "Bounced") and the foreground text hue (a real, if modest, ~1.5:1
  // separation that does survive simulation) are what actually carry it.
  expired: { bg: '#fef3c7', color: adminColors.warningAlertBg },
  cancelled: { bg: '#f7f7f8', color: '#616161' },
  failed: { bg: '#fef2f2', color: '#991b1b' },
};

// The `hint` (Tooltip text, reachable on hover AND keyboard focus — see
// InviteStatusChip) for a given email_status, independent of whatever
// `status` (PENDING/EXPIRED/...) the invite itself is currently in —
// shared so EXPIRED doesn't lose this the way it used to (a bounced,
// suppressed, or failed send that then expired used to render an
// identical, non-interactive "Expired …" chip with no way to tell it apart
// from one that expired cleanly). A long `last_error` (e.g. "the email
// server rejected the recipient …") lives here rather than in the visible
// label: it used to be crammed into the chip label itself and silently
// clipped by the chip's maxWidth with no way to read the rest.
function emailStatusHint(inv) {
  switch (inv.email_status) {
    case 'FAILED':
      return inv.last_error ? ensureSentence(inv.last_error) : 'Delivery failed.';
    case 'BOUNCED':
      return inv.last_error
        ? ensureSentence(inv.last_error)
        : "The recipient's mail server permanently rejected this — the address may be mistyped or no longer exist. Check it, then resend.";
    case 'SUPPRESSED':
      // The one hint that most needs to stop an admin from mashing Resend
      // forever: Resend is refusing to even attempt delivery, and the
      // resend button can't fix that. When Resend gave us a specific
      // reason (`last_error`), that reason IS the explanation — it
      // replaces the generic "usually from…" guess rather than both
      // running together mid-sentence.
      return inv.last_error
        ? `Resend won't attempt delivery to this address — it's on their suppression list. ${ensureSentence(inv.last_error)} Clear it in the Resend dashboard first; resending here won't help until you do.`
        : "Resend won't attempt delivery to this address — it's on their suppression list, usually from an earlier hard bounce or spam complaint. Clear it in the Resend dashboard first; resending here won't help until you do.";
    case 'COMPLAINED':
      return "The recipient's mail provider reported this as spam after it was delivered. Resend isn't offered — sending again to someone who just did that is a compliance risk. Reach out another way if they still need access.";
    case 'DELIVERED':
      return "Resend confirmed the recipient's mail server accepted this — they just haven't clicked the invite link yet.";
    case 'DELAYED':
      return "Resend hit a temporary problem delivering this and is retrying automatically. Nothing to do yet — resend manually only if it's still delayed after a day.";
    case 'SENT':
      return "Accepted by Resend and on its way. We haven't gotten delivery confirmation back yet — that can take a few minutes and isn't a problem on its own.";
    case 'SKIPPED':
      return "No email was sent — delivery isn't configured on this server. Share the invite link directly, or resend once delivery is configured.";
    case 'UNSENT':
      return "This invitation was saved, but a delivery attempt hasn't completed yet.";
    default:
      return null;
  }
}

// Which email_status values are worth attaching a hint to on an EXPIRED
// invite specifically. PENDING gets emailStatusHint() unconditionally —
// every value says something useful about a still-open invite — but once
// an invite has expired, "accepted by Resend and on its way" or "they
// just haven't clicked yet" no longer describes anything real (there's no
// live link left to click), so repeating those on an expired row would be
// stale, not informative. The ones that genuinely still matter after
// expiry are the same ones that matter anywhere: an actual problem or
// pending explanation the admin might otherwise have no way to see —
// exactly the FAILED/BOUNCED/SUPPRESSED/COMPLAINED/DELAYED set this file
// already colors red or grey rather than the "nothing wrong" indigo/green.
const REASON_BEARING_EMAIL_STATUSES = new Set(['FAILED', 'BOUNCED', 'SUPPRESSED', 'COMPLAINED', 'DELAYED']);

// Exported so InvitesPage.test.js can assert on the key/hint decision
// directly (e.g. "DELIVERED resolves to the same `key` as ACCEPTED") rather
// than through jsdom computed-style/DOM-traversal assertions — mirrors
// SafetyFlagsView's SEVERITY_CONFIG/STATUS_CONFIG being exported for the
// same reason.
export function inviteStatusInfo(inv) {
  if (inv.status === 'ACCEPTED') return { key: 'accepted', text: 'Accepted' };
  if (inv.status === 'CANCELLED') return { key: 'cancelled', text: 'Cancelled' };

  if (inv.status === 'EXPIRED') {
    // Label/color stay the lifecycle truth — "the window closed" — a
    // reason-bearing email_status hint rides along underneath it rather
    // than changing what's visible at a glance. This is the fix for a
    // real bug: EXPIRED used to return before email_status was ever read,
    // so a bounced/failed/suppressed send that then expired rendered a
    // pixel-identical, non-interactive chip to one that expired cleanly.
    return {
      key: 'expired',
      text: `Expired ${inv.expiry_timestamp ? new Date(inv.expiry_timestamp).toLocaleDateString() : ''}`.trim(),
      hint: REASON_BEARING_EMAIL_STATUSES.has(inv.email_status) ? emailStatusHint(inv) : null,
    };
  }

  // PENDING — email_status decides the rest, hint included unconditionally.
  const hint = emailStatusHint(inv);
  const expires = inv.expiry_timestamp ? new Date(inv.expiry_timestamp).toLocaleDateString() : '—';
  if (inv.email_status === 'FAILED') return { key: 'failed', text: 'Failed · check delivery', hint };
  if (inv.email_status === 'BOUNCED') return { key: 'failed', text: 'Bounced · check address', hint };
  if (inv.email_status === 'SUPPRESSED') return { key: 'failed', text: 'Suppressed · blocked in Resend', hint };
  if (inv.email_status === 'COMPLAINED') return { key: 'cancelled', text: 'Marked as spam', hint };
  // DELIVERED reuses `accepted`'s exact green rather than a second one —
  // measured, a distinct "delivered" green and `accepted`'s (#2e7d32 vs
  // #047857) sit at ~1.07:1 contrast from each other, indistinguishable at
  // chip size. The label ("Delivered · expires …" vs "Accepted") and the
  // Actions column (Resend+Revoke vs neither) already do all the real
  // disambiguating; a second green was decorative, not load-bearing, and a
  // third hue here would muddy the four-color system above for no gain.
  if (inv.email_status === 'DELIVERED') return { key: 'accepted', text: `Delivered · expires ${expires}`, hint };
  if (inv.email_status === 'DELAYED') return { key: 'pending', text: `Delayed · expires ${expires}`, hint };
  if (inv.email_status === 'SENT') return { key: 'pending', text: `Sent · expires ${expires}`, hint };
  return { key: 'pending', text: `Pending · expires ${expires}`, hint };
}

function InviteStatusChip({ invitation }) {
  const info = inviteStatusInfo(invitation);
  const style = INVITE_STATUS_STYLES[info.key];
  const chip = (
    <Chip
      label={info.text}
      size="small"
      // Focusable exactly when there's something extra to reveal — a
      // Tooltip firing on hover alone is invisible to keyboard/screen-
      // reader use (see D2/D8); tabIndex makes the chip itself a stop so
      // the same Tooltip fires on focus too. data-testid gives tests a
      // stable, non-DOM-traversal way to grab this exact focusable node
      // (see InvitesPage.test.js's D2 coverage) rather than reaching for
      // the label text's ancestor.
      tabIndex={info.hint ? 0 : undefined}
      data-testid={info.hint ? 'invite-status-chip' : undefined}
      sx={{
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 600,
        maxWidth: 320,
        ...(info.hint && {
          '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
        }),
      }}
    />
  );
  return info.hint ? <Tooltip title={info.hint}>{chip}</Tooltip> : chip;
}

// Handles both response shapes GET /api/invitations can return: a bare
// array (no `q` sent — legacy/back-compat) or `{total, page, page_size,
// invitations}` (whenever `q` is present, including `q=""`). This page
// always sends `q` explicitly (see fetchInvitations below) specifically so
// it always gets the richer shape and a real `total` for pagination — this
// normalizer is the defensive fallback for the bare-array shape regardless.
function normalizeInvitationsResponse(data) {
  if (Array.isArray(data)) {
    return { invitations: data, total: data.length };
  }
  const invitations = data?.invitations || [];
  return { invitations, total: typeof data?.total === 'number' ? data.total : invitations.length };
}

function InvitesSection({ currentUser, notify, confirmAction, onOpenAddUserDialog }) {
  const navigate = useNavigate();
  const [qs, setQueryState] = useQueryState(INVITES_QUERY_DEFAULTS);

  const [invitations, setInvitations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // invitation id being resent/revoked

  // Copy below branches on the actual role, not a combined "school-scoped"
  // check (D7) — GET /api/invitations scopes SCHOOL_ADMIN and TEACHER
  // differently even though both are school-locked: a SCHOOL_ADMIN sees
  // every invite anyone at their school has sent, a TEACHER sees only
  // their own. Collapsing the two into one copy branch either understated
  // the admin's visibility or overstated the teacher's.
  const isSchoolAdminRole = isSchoolAdmin(currentUser?.role);
  const isTeacherRole = isTeacher(currentUser?.role);

  const fetchInvitations = useCallback(
    (signal) => {
      setLoading(true);
      setError('');
      // `q` is always sent (even empty) so the response is always the
      // `{total, ...}` shape — see normalizeInvitationsResponse above.
      const params = new URLSearchParams({ page: String(qs.page), page_size: String(INVITES_PAGE_SIZE), q: qs.q || '' });
      if (qs.status) params.set('status', qs.status);
      return authFetch(`${config.API_BASE_URL}/api/invitations?${params.toString()}`, {}, { signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || signal?.aborted) return; // 401 — already redirected
          const normalized = normalizeInvitationsResponse(data);
          setInvitations(normalized.invitations);
          setTotal(normalized.total);
          const newTotalPages = Math.max(1, Math.ceil(normalized.total / INVITES_PAGE_SIZE));
          if (qs.page > newTotalPages) setQueryState({ page: newTotalPages });
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || signal?.aborted) return;
          setError(`Couldn't load invites. ${describeApiError(err)}`);
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [qs.page, qs.q, qs.status, navigate, setQueryState]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchInvitations(controller.signal);
    return () => controller.abort();
  }, [fetchInvitations]);

  const handleResend = async (id) => {
    setActionLoading(id);
    try {
      // Resets the token/expiry and resends on the SAME row — replaces
      // rather than duplicates (PATCH /invitations/{id}/resend, see
      // app/routes/invitations.py's _regenerate_and_send).
      const res = await authFetch(`${config.API_BASE_URL}/api/invitations/${id}/resend`, { method: 'PATCH' });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify('Invitation resent.', 'success');
      fetchInvitations();
    } catch (err) {
      notify(`This invitation couldn't be resent. ${describeAdminError(err)}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const doRevoke = async (id) => {
    setActionLoading(id);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/invitations/${id}/cancel`, { method: 'PATCH' });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify('Invitation revoked.', 'success');
      fetchInvitations();
    } catch (err) {
      notify(`This invitation couldn't be revoked. ${describeAdminError(err)}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = (id) => {
    confirmAction({
      title: 'Revoke invitation',
      body: 'Revoke this invitation? The link will stop working, and the recipient will no longer be able to use it to join.',
      confirmLabel: 'Revoke invitation',
      onConfirm: () => doRevoke(id),
    });
  };

  const columns = useMemo(
    () => [
      {
        key: 'invitee_email',
        label: 'Email',
        // "Sent" used to be its own column — folded in here as a secondary
        // line instead. That, plus capping Status's width just below, is
        // what keeps this table from overflowing its container at
        // 1024px/768px with no visible way to reach Resend/Revoke
        // (admin-console-v2-phase2 review D5); Invites' Status column runs
        // noticeably wider than e.g. StaffPage's own columns, which is why
        // this table needed it and StaffPage didn't.
        render: (row) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }} noWrap>
              {row.invitee_email}
            </Typography>
            <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>
              Sent {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
            </Typography>
          </Box>
        ),
      },
      { key: 'role', label: 'Role', render: (row) => <RoleChip role={row.role} /> },
      {
        key: 'school',
        label: 'School',
        render: (row) => (
          <Typography variant="body2" sx={{ color: row.school ? adminColors.ink : adminColors.inkSoft }}>
            {row.school || '—'}
          </Typography>
        ),
      },
      { key: 'status', label: 'Status', width: 200, render: (row) => <InviteStatusChip invitation={row} /> },
      {
        key: 'actions',
        label: 'Actions',
        align: 'right',
        render: (row) => {
          const busy = actionLoading === row.id;
          // PATCH /invitations/{id}/resend itself only gates on
          // invitation.status being PENDING or EXPIRED — nothing about
          // email_status — so this mirrors that exactly, with one
          // deliberate UI-only exception: COMPLAINED. The recipient marked
          // an earlier send as spam; offering a button that re-sends to
          // them is a compliance problem, not a UX one, even though the
          // API would accept the request. Every other email_status
          // (including DELIVERED/SENT — the invite most likely to actually
          // need a manual nudge, since it reached them fine and they just
          // haven't clicked yet) keeps Resend exactly as it always has.
          const canResend =
            (row.status === 'PENDING' || row.status === 'EXPIRED') && row.email_status !== 'COMPLAINED';
          const canRevoke = row.status === 'PENDING';
          if (!canResend && !canRevoke) {
            return <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>—</Typography>;
          }
          return (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {canResend && (
                <Button size="small" variant="outlined" onClick={() => handleResend(row.id)} disabled={busy} sx={TOUCH_HIT_SX}>
                  {busy ? <CircularProgress size={14} /> : 'Resend'}
                </Button>
              )}
              {canRevoke && (
                <Button size="small" variant="outlined" color="error" onClick={() => handleRevoke(row.id)} disabled={busy} sx={TOUCH_HIT_SX}>
                  Revoke
                </Button>
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleResend/handleRevoke are plain closures re-created every render (not useCallback), not state; only actionLoading (which they read) needs to trigger a recompute, and it's already listed
    [actionLoading]
  );

  const totalPages = Math.max(1, Math.ceil(total / INVITES_PAGE_SIZE));

  const emptyStateNode = (qs.q || qs.status) ? (
    <EmptyState
      icon={<HowToRegIcon />}
      title="No matches"
      description="Try a different search, or clear filters to see every invite."
      action={
        <Button variant="outlined" onClick={() => setQueryState({ ...INVITES_QUERY_DEFAULTS })} sx={TOUCH_HIT_SX}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<HowToRegIcon />}
      title="No invites yet"
      description='Use "Send Invitation" to invite someone by email.'
      action={
        <Button variant="contained" onClick={onOpenAddUserDialog} sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark }, ...TOUCH_HIT_SX }}>
          Send Invitation
        </Button>
      }
    />
  );

  return (
    <Box component="section">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink }}>Invites</Typography>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
            {isTeacherRole
              ? "Invitations you've sent."
              : isSchoolAdminRole
              ? 'Every invitation your school has sent.'
              : 'Every invitation sent, across every school you can see.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onOpenAddUserDialog}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark }, ...TOUCH_HIT_SX }}
        >
          Send Invitation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchInvitations()}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <FilterBar
        queryState={qs}
        setQueryState={setQueryState}
        defaults={INVITES_QUERY_DEFAULTS}
        searchKey="q"
        searchPlaceholder="Search by email…"
        resetOnSearch={['page']}
        sx={{ mb: 2.5 }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="invite-status-filter-label">Status</InputLabel>
          <Select
            labelId="invite-status-filter-label"
            label="Status"
            value={qs.status}
            onChange={(e) => setQueryState({ status: e.target.value, page: INVITES_QUERY_DEFAULTS.page })}
            sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
          >
            {INVITE_STATUS_FILTERS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={invitations}
        loading={loading}
        emptyState={emptyStateNode}
      />

      {total > 0 && totalPages > 1 && (
        <Stack alignItems="center" mt={3}>
          <Pagination
            count={totalPages}
            page={qs.page}
            onChange={(_, v) => setQueryState({ page: v })}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// Note on role scoping: sign-up requests are MASTER_ADMIN-only (both the
// section below and its GET /api/access-inquiries call are gated on that);
// every STAFF_ROLES member (including TEACHER) gets the Invites section.
// A TEACHER's own invite-role restriction (students only) isn't enforced
// here — it lives where "Send Invitation" actually opens, in
// AdminDashboardV2.js's Add User dialog (`isTeacher` pins `role` to
// STUDENT there), which this page reuses via `onOpenAddUserDialog` rather
// than duplicating.
export default function InvitesPage({ currentUser, notify, confirmAction, onOpenAddUserDialog }) {
  const masterAdmin = isMasterAdmin(currentUser?.role);
  // See InvitesSection's own isSchoolAdminRole/isTeacherRole comment (D7) —
  // same reasoning, same two roles that need distinct copy here too.
  const schoolAdminRole = isSchoolAdmin(currentUser?.role);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: adminColors.ink }}>Invites</Typography>
        <Typography variant="body2" sx={{ color: adminColors.inkSoft, mt: 0.5 }}>
          {masterAdmin
            ? 'Sign-up requests and email invitations, in one place.'
            : schoolAdminRole
            ? 'Track every invitation your school has sent.'
            : "Track the invitations you've sent."}
        </Typography>
      </Box>

      {masterAdmin && (
        <>
          <SignupRequestsSection currentUser={currentUser} notify={notify} />
          <Divider sx={{ mb: 4, borderColor: 'rgba(24,24,27,0.08)' }} />
        </>
      )}

      <InvitesSection
        currentUser={currentUser}
        notify={notify}
        confirmAction={confirmAction}
        onOpenAddUserDialog={onOpenAddUserDialog}
      />
    </Box>
  );
}
